import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Divider,
  Chip,
} from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAppThemeApi, updateAppThemeApi, resetAppThemeApi } from "../../../redux/api/api";
import { THEME_PRESETS, PALETTE_FIELDS, FONT_FAMILY_LABELS } from "./THEME_PRESETS";

const HEX_PATTERN = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const FALLBACK_FONT_FAMILIES = ["SYSTEM", "SERIF", "MONOSPACE", "ROUNDED"];

const emptyPalette = () =>
  PALETTE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

/**
 * Single-document "settings" page (no list — there is only ever one live
 * theme) for the React Native customer app's remote brand override:
 * palette + font family, published from here and read by the app on every
 * launch (GET /api/app/theme). Colour-picker pattern mirrors
 * CustomerAppServiceForm.js (input[type=color] + hex TextField).
 *
 * A field left blank means "let the app use its own built-in default" — the
 * customer app's src/theme/useTheme.ts only ever overrides what's explicitly
 * set here, and ignores this document entirely while "Publish" is off.
 */
const AppThemeForm = () => {
  const [palette, setPalette] = useState(emptyPalette());
  const [fontFamily, setFontFamily] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [presetId, setPresetId] = useState(null);
  const [fontFamilies, setFontFamilies] = useState(FALLBACK_FONT_FAMILIES);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getAppThemeApi()
      .then((res) => {
        const info = res?.data?.info;
        const theme = info?.theme;
        if (theme) {
          setPalette({ ...emptyPalette(), ...(theme.palette || {}) });
          setFontFamily(theme.typography?.fontFamily || "");
          setIsActive(Boolean(theme.isActive));
          setPresetId(theme.presetId || null);
          setUpdatedAt(theme.updatedAt || null);
        }
        if (info?.fontFamilies?.length) setFontFamilies(info.fontFamilies);
      })
      .catch((err) => toast.error(err?.response?.data?.message || "Couldn't load the current theme."))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const updateField = (key, value) => setPalette((prev) => ({ ...prev, [key]: value }));

  const validate = () => {
    for (const field of PALETTE_FIELDS) {
      const v = (palette[field.key] || "").trim();
      if (v && !HEX_PATTERN.test(v)) {
        return `${field.label} must be a hex colour like #C1922E (or left blank).`;
      }
    }
    return null;
  };

  const buildPalettePayload = () =>
    PALETTE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: (palette[f.key] || "").trim() || null }), {});

  /** One click: fill every field from a named preset, mark it live, and publish immediately. */
  const applyAndPublish = async (preset) => {
    setSaving(true);
    try {
      await updateAppThemeApi({
        palette: preset.palette,
        typography: { fontFamily: preset.fontFamily },
        isActive: true,
        presetId: preset.id,
      });
      setPalette({ ...emptyPalette(), ...preset.palette });
      setFontFamily(preset.fontFamily || "");
      setIsActive(true);
      setPresetId(preset.id);
      toast.success(`${preset.label} is now live on the customer app.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to publish this preset.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const error = validate();
    if (error) return toast.error(error);
    setSaving(true);
    try {
      await updateAppThemeApi({
        palette: buildPalettePayload(),
        typography: { fontFamily: fontFamily || null },
        isActive,
        presetId: null, // hand-edited — no longer exactly matches a named preset
      });
      setPresetId(null);
      toast.success(isActive ? "Theme saved and published." : "Theme saved (not live — toggle Publish to go live).");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to save the theme.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await resetAppThemeApi();
      setPalette(emptyPalette());
      setFontFamily("");
      setIsActive(false);
      setPresetId(null);
      toast.success("Reverted — the app will use its own built-in default theme.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Unable to reset the theme.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography>Loading theme…</Typography>
      </Box>
    );
  }

  const previewPrimary = HEX_PATTERN.test(palette.primary || "") ? palette.primary : "#C1922E";
  const previewSecondary = HEX_PATTERN.test(palette.secondary || "") ? palette.secondary : "#D8AC55";
  const previewAccent = HEX_PATTERN.test(palette.accent || "") ? palette.accent : "#EFDCA6";
  const previewBackground = HEX_PATTERN.test(palette.background || "") ? palette.background : "#FCF9F0";
  const previewSurface = HEX_PATTERN.test(palette.surface || "") ? palette.surface : "#FFFFFF";
  const previewTextPrimary = HEX_PATTERN.test(palette.textPrimary || "") ? palette.textPrimary : "#2A2110";
  const previewTextSecondary = HEX_PATTERN.test(palette.textSecondary || "") ? palette.textSecondary : "#7A6A4E";
  const previewBorder = HEX_PATTERN.test(palette.border || "") ? palette.border : "#EFE6D2";

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>Customer App Theme</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>App Configuration</li>
          <li>App Theme</li>
        </ul>
      </div>

      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Card sx={{ flex: "1 1 620px" }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                One-click presets
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Fills every colour below and publishes immediately — the whole customer app re-skins on its next
                launch (or the next time it refreshes its config), no app update required.
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                {THEME_PRESETS.map((preset) => (
                  <Button
                    key={preset.id}
                    variant={presetId === preset.id && isActive ? "contained" : "outlined"}
                    disabled={saving}
                    onClick={() => applyAndPublish(preset)}
                    startIcon={
                      <Box
                        sx={{ width: 14, height: 14, borderRadius: "50%", bgcolor: preset.swatch, border: "1px solid rgba(0,0,0,0.15)" }}
                      />
                    }
                  >
                    {preset.label}
                    {presetId === preset.id && isActive ? " — Live" : ""}
                  </Button>
                ))}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Palette
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Leave any field blank to use the app's built-in default for that colour.
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {PALETTE_FIELDS.map((field) => (
                  <Box key={field.key} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography sx={{ width: 150, flexShrink: 0, fontSize: 14 }}>{field.label}</Typography>
                    <input
                      type="color"
                      value={HEX_PATTERN.test(palette[field.key] || "") && palette[field.key].length === 7 ? palette[field.key] : "#ffffff"}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      style={{ width: 40, height: 34, border: "1px solid #ddd", borderRadius: 6, cursor: "pointer", padding: 0 }}
                    />
                    <TextField
                      size="small"
                      placeholder="App default"
                      value={palette[field.key] || ""}
                      onChange={(e) => updateField(field.key, e.target.value)}
                      sx={{ width: 130 }}
                    />
                    {palette[field.key] ? (
                      <Button size="small" onClick={() => updateField(field.key, "")}>
                        Clear
                      </Button>
                    ) : null}
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Font family
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Limited to fonts already built into Android/iOS, so this applies instantly with no app update. A
                custom uploaded typeface would need its font files shipped in a new app release — ask engineering to
                add it as a preset here first.
              </Typography>
              <FormControl sx={{ minWidth: 240 }}>
                <InputLabel>Font Family</InputLabel>
                <Select label="Font Family" value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}>
                  <MenuItem value="">App default</MenuItem>
                  {fontFamilies.map((f) => (
                    <MenuItem key={f} value={f}>
                      {FONT_FAMILY_LABELS[f] || f}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <FormControlLabel
                control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
                label="Publish (live on the customer app)"
              />
              {updatedAt ? (
                <Chip size="small" variant="outlined" label={`Last updated ${new Date(updatedAt).toLocaleString()}`} />
              ) : null}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
              <Button onClick={handleReset} disabled={saving} color="warning">
                Reset to App Default
              </Button>
              <Button variant="contained" disabled={saving} onClick={handleSave}>
                {saving ? "Saving…" : "Save Theme"}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Live preview */}
        <Card sx={{ flex: "0 1 300px", position: "sticky", top: 16 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Customer App Preview
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Approximate — the app owns the real layout and spacing.
            </Typography>
            <Box sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #eee" }}>
              <Box
                sx={{
                  p: 2,
                  background: `linear-gradient(135deg, ${previewPrimary}, ${previewSecondary}, ${previewAccent})`,
                }}
              >
                <Typography sx={{ color: "#fff", fontSize: 12, opacity: 0.9 }}>Good morning, 👋</Typography>
                <Typography sx={{ color: "#fff", fontSize: 20, fontWeight: 800 }}>Guest</Typography>
              </Box>
              <Box sx={{ bgcolor: previewBackground, p: 2 }}>
                <Box
                  sx={{
                    bgcolor: previewSurface,
                    border: `1px solid ${previewBorder}`,
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Typography sx={{ color: previewTextPrimary, fontWeight: 700, fontSize: 13 }}>
                    Ride smarter, save more
                  </Typography>
                  <Typography sx={{ color: previewTextSecondary, fontSize: 11 }}>
                    Flat 20% off your first ride
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "inline-block",
                    bgcolor: previewPrimary,
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 1.5,
                    py: 0.75,
                  }}
                >
                  Book Now
                </Box>
              </Box>
            </Box>
            {!isActive ? (
              <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 2 }}>
                “Publish” is off — this theme is saved but the customer app is still using its built-in default.
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default AppThemeForm;
